import React, { useEffect, useState } from "react";
import {
  fetchSmoke, fetchAqi, fetchWindField, fetchLightning, fetchAircraft,
  fetchAlerts, fetchInfoPins, fetchFieldPhotos,
} from "../../lib/api";
import SmokeLayer from "./SmokeLayer";
import AqiLayer from "./AqiLayer";
import WindLayer from "./WindLayer";
import RadarLayer from "./RadarLayer";
import LightningLayer from "./LightningLayer";
import AircraftLayer from "./AircraftLayer";
import WeatherAlertsLayer from "./WeatherAlertsLayer";
import InfoPinsLayer from "./InfoPinsLayer";
import FieldPhotosLayer from "./FieldPhotosLayer";
import { CaptureMap, MapSearchBox, SearchMarker } from "./MapSearchControl";
import { ScreenshotButton } from "./MapScreenshot";
import { MeasureLayer, MeasureButton } from "./MeasureTool";

const CB = "flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer";

function Check({ testid, checked, onChange, accent, children }) {
  return (
    <label className={CB} data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={onChange} className={accent} />
      {children}
    </label>
  );
}

// Live-map layer toolkit for the incident detail map: Layers dropdown
// (aircraft, wx, smoke, AQI, wind, radar, lightning, info pins, field photos)
// plus screenshot + measure buttons. Returns { layers, toolbar }.
export default function useDetailMapExtras(county) {
  const [layersOpen, setLayersOpen] = useState(false);
  const [mapInst, setMapInst] = useState(null);
  const [searchMarker, setSearchMarker] = useState(null);
  const [measureActive, setMeasureActive] = useState(false);
  const [measureTotal, setMeasureTotal] = useState(null);
  const [measureDrawing, setMeasureDrawing] = useState(false);
  const [measureFinishTick, setMeasureFinishTick] = useState(0);
  const [showAircraft, setShowAircraft] = useState(true);
  const [aircraft, setAircraft] = useState([]);
  const [showWx, setShowWx] = useState(false);
  const [wxAlerts, setWxAlerts] = useState([]);
  const [showSmoke, setShowSmoke] = useState(false);
  const [smoke, setSmoke] = useState(null);
  const [showAqi, setShowAqi] = useState(false);
  const [aqi, setAqi] = useState(null);
  const [showWind, setShowWind] = useState(false);
  const [windField, setWindField] = useState(null);
  const [showRadar, setShowRadar] = useState(false);
  const [showLightning, setShowLightning] = useState(false);
  const [lightning, setLightning] = useState([]);
  const [showInfoPins, setShowInfoPins] = useState(true);
  const [infoPins, setInfoPins] = useState([]);
  const [showFieldPhotos, setShowFieldPhotos] = useState(true);
  const [fieldPhotos, setFieldPhotos] = useState([]);

  useEffect(() => {
    if (!measureActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMeasureActive(false);
        setMeasureTotal(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [measureActive]);

  useEffect(() => {
    fetchInfoPins(county).then(setInfoPins).catch(() => {});
    fetchFieldPhotos().then(setFieldPhotos).catch(() => {});
  }, [county]);

  useEffect(() => {
    if (!showAircraft) return;
    const load = () => fetchAircraft(county).then((d) => setAircraft(d.aircraft || d || [])).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [showAircraft, county]);

  useEffect(() => {
    if (showWx && !wxAlerts.length) fetchAlerts(county).then(setWxAlerts).catch(() => {});
  }, [showWx, wxAlerts.length, county]);

  useEffect(() => {
    if (showSmoke && !smoke) fetchSmoke().then(setSmoke).catch(() => {});
  }, [showSmoke, smoke]);

  useEffect(() => {
    if (showAqi && !aqi) fetchAqi().then(setAqi).catch(() => {});
  }, [showAqi, aqi]);

  useEffect(() => {
    if (showWind && !windField) fetchWindField(county).then(setWindField).catch(() => {});
  }, [showWind, windField, county]);

  useEffect(() => {
    if (!showLightning) return;
    const load = () => fetchLightning(60).then((d) => setLightning(d.strikes || [])).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [showLightning]);

  const layers = (
    <>
      <CaptureMap onMap={setMapInst} />
      <SearchMarker marker={searchMarker} />
      {measureActive && <MeasureLayer key="measure" onTotal={setMeasureTotal} onDrawing={setMeasureDrawing} finishTick={measureFinishTick} />}
      {showAircraft && <AircraftLayer aircraft={aircraft} />}
      {showWx && <WeatherAlertsLayer alerts={wxAlerts} />}
      {showSmoke && <SmokeLayer smoke={smoke} />}
      {showRadar && <RadarLayer />}
      {showLightning && <LightningLayer strikes={lightning} />}
      {showWind && windField && <WindLayer points={windField} />}
      {showAqi && <AqiLayer aqi={aqi} />}
      {showInfoPins && <InfoPinsLayer pins={infoPins} />}
      {showFieldPhotos && <FieldPhotosLayer photos={fieldPhotos} />}
    </>
  );

  const toolbar = (
    <div data-testid="detail-map-toolbar">
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          data-testid="detail-layers-button"
          onClick={() => setLayersOpen((v) => !v)}
          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 backdrop-blur-xl border-tac-border text-tac-text hover:border-tac-borderBright transition-colors whitespace-nowrap"
        >
          Layers {layersOpen ? "▴" : "▾"}
        </button>
        {mapInst && <MapSearchBox map={mapInst} onMarker={setSearchMarker} />}
        <ScreenshotButton map={mapInst} />
        <MeasureButton
          active={measureActive}
          total={measureTotal}
          drawing={measureDrawing}
          onFinish={() => setMeasureFinishTick((t) => t + 1)}
          onToggle={() => {
            setMeasureActive((v) => !v);
            setMeasureTotal(null);
            setMeasureDrawing(false);
          }}
        />
      </div>
      {layersOpen && (
        <div className="mt-1.5 w-44 bg-tac-bg/90 backdrop-blur-xl border border-tac-border rounded-sm p-2 space-y-1.5">
          <Check testid="detail-aircraft-toggle" checked={showAircraft} onChange={() => setShowAircraft((v) => !v)} accent="accent-[#4CC9F0]">
            Aircraft{showAircraft && aircraft.length ? ` (${aircraft.length})` : ""}
          </Check>
          <Check testid="detail-wx-toggle" checked={showWx} onChange={() => setShowWx((v) => !v)} accent="accent-[#E63946]">
            NWS Wx Alerts
          </Check>
          <Check testid="detail-smoke-toggle" checked={showSmoke} onChange={() => setShowSmoke((v) => !v)} accent="accent-[#6B7280]">
            Smoke (NOAA)
          </Check>
          <Check testid="detail-aqi-toggle" checked={showAqi} onChange={() => setShowAqi((v) => !v)} accent="accent-[#00E400]">
            AQI Monitors
          </Check>
          <Check testid="detail-wind-toggle" checked={showWind} onChange={() => setShowWind((v) => !v)} accent="accent-[#8FA99B]">
            Live Wind
          </Check>
          <Check testid="detail-radar-toggle" checked={showRadar} onChange={() => setShowRadar((v) => !v)} accent="accent-[#4CC9F0]">
            Rain Radar
          </Check>
          <Check testid="detail-lightning-toggle" checked={showLightning} onChange={() => setShowLightning((v) => !v)} accent="accent-[#FFD60A]">
            Lightning{showLightning && lightning.length ? ` (${lightning.length})` : ""}
          </Check>
          <Check testid="detail-info-pins-toggle" checked={showInfoPins} onChange={() => setShowInfoPins((v) => !v)} accent="accent-[#3B9DF2]">
            Info Pins{infoPins.length ? ` (${infoPins.length})` : ""}
          </Check>
          <Check testid="detail-field-photos-toggle" checked={showFieldPhotos} onChange={() => setShowFieldPhotos((v) => !v)} accent="accent-[#E9C46A]">
            Field Photos{fieldPhotos.length ? ` (${fieldPhotos.length})` : ""}
          </Check>
          <div className="pt-1.5 border-t border-tac-border/60">
            <ScreenshotButton map={mapInst} variant="row" />
          </div>
        </div>
      )}
    </div>
  );

  return { layers, toolbar };
}
